import { DashboardStats, ActiveSession } from '@/hooks/useDashboardData';
import { ProgressRing } from '@/components/ui/progress-ring';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { Clock, LayoutDashboard, Activity, ArrowRight, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface DashboardHeaderProps {
  userName: string | null;
  stats: DashboardStats;
  activeSessions?: ActiveSession[];
}

export const DashboardHeader = ({ userName, stats, activeSessions = [] }: DashboardHeaderProps) => {
  const navigate = useNavigate();
  
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  };

  const formatLastActivity = () => {
    if (!stats.lastActivity) return 'Noch keine Aktivität';
    try {
      return `vor ${formatDistanceToNow(new Date(stats.lastActivity), { locale: de })}`;
    } catch {
      return 'Kürzlich';
    }
  };

  // Find the most relevant session to continue
  const getMostRelevantSession = () => {
    if (activeSessions.length === 0) return null;
    
    // Prioritize phase 1 sessions, then by most recently updated
    const phase1Sessions = activeSessions.filter(s => s.phase === 1);
    if (phase1Sessions.length > 0) {
      return phase1Sessions.sort((a, b) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )[0];
    }
    
    // Otherwise, return the most recently updated
    return [...activeSessions].sort((a, b) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )[0];
  };

  const relevantSession = getMostRelevantSession();

  return (
    <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
      {/* Header with greeting */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-foreground">
            {getGreeting()}{userName ? `, ${userName}` : ''}
          </h1>
          <p className="text-muted-foreground mt-1 sm:mt-1.5 text-sm sm:text-base">
            Willkommen in deinem persönlichen Entscheidungs-Dashboard
          </p>
        </div>
        
        {/* Focus Tip - Continue where you left off */}
        {relevantSession && (
          <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20 animate-fade-in">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs text-muted-foreground">Weitermachen</p>
              <p className="text-xs sm:text-sm font-medium text-foreground truncate">
                {relevantSession.summary || relevantSession.decision_type || 'Entscheidung'}
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate(`/app/session/${relevantSession.id}`)}
              className="hover:bg-primary/10 hover:text-primary h-8 w-8 sm:h-9 sm:w-9 p-0"
              aria-label="Entscheidung fortsetzen"
            >
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Stats Grid - horizontal scroll on mobile */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {/* Open Decisions Card */}
        <div className="group relative bg-card border border-border/50 rounded-xl sm:rounded-2xl p-3 sm:p-5 transition-all duration-500 hover:border-primary/20 hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.15)] hover:-translate-y-0.5 overflow-hidden">
          {/* Background gradient on hover */}
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-br from-primary/5 via-transparent to-transparent" aria-hidden="true" />
          
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500 flex-shrink-0">
                <LayoutDashboard className="w-4 h-4 sm:w-5 sm:h-5 text-primary group-hover:text-primary-foreground transition-colors" />
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground font-medium hidden sm:inline">Offene Entscheidungen</span>
              <span className="text-xs text-muted-foreground font-medium sm:hidden">Offen</span>
            </div>
            <span className="text-2xl sm:text-3xl font-serif font-semibold text-foreground group-hover:text-primary transition-colors">{stats.openDecisions}</span>
          </div>
          
          {/* Bottom accent line */}
          <div className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-primary to-primary/30 transition-all duration-500" aria-hidden="true" />
        </div>

        {/* Progress Card */}
        <div className="group relative bg-card border border-border/50 rounded-xl sm:rounded-2xl p-3 sm:p-5 transition-all duration-500 hover:border-primary/20 hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.15)] hover:-translate-y-0.5 overflow-hidden">
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-br from-primary/5 via-transparent to-transparent" aria-hidden="true" />
          
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500 flex-shrink-0">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-primary group-hover:text-primary-foreground transition-colors" />
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground font-medium hidden sm:inline">Ø Fortschritt</span>
              <span className="text-xs text-muted-foreground font-medium sm:hidden">Fortschritt</span>
            </div>
            <ProgressRing 
              progress={stats.avgProgress} 
              size={40} 
              strokeWidth={3}
              className="sm:hidden"
              aria-label={`Durchschnittlicher Fortschritt: ${stats.avgProgress}%`}
            >
              <span className="text-xs font-semibold text-foreground group-hover:text-primary transition-colors" aria-hidden="true">{stats.avgProgress}%</span>
            </ProgressRing>
            <ProgressRing 
              progress={stats.avgProgress} 
              size={48} 
              strokeWidth={4}
              className="hidden sm:flex"
              aria-label={`Durchschnittlicher Fortschritt aller Entscheidungen: ${stats.avgProgress}%`}
            >
              <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors" aria-hidden="true">{stats.avgProgress}%</span>
            </ProgressRing>
          </div>
          
          <div className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-primary to-primary/30 transition-all duration-500" aria-hidden="true" />
        </div>

        {/* Last Activity Card */}
        <div className="group relative bg-card border border-border/50 rounded-xl sm:rounded-2xl p-3 sm:p-5 transition-all duration-500 hover:border-primary/20 hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.15)] hover:-translate-y-0.5 overflow-hidden">
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-br from-primary/5 via-transparent to-transparent" aria-hidden="true" />
          
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-500 flex-shrink-0">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary group-hover:text-primary-foreground transition-colors" />
              </div>
              <span className="text-xs sm:text-sm text-muted-foreground font-medium hidden sm:inline">Letzte Aktivität</span>
              <span className="text-xs text-muted-foreground font-medium sm:hidden">Aktivität</span>
            </div>
            <span className="text-xs sm:text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate">{formatLastActivity()}</span>
          </div>
          
          <div className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-primary to-primary/30 transition-all duration-500" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
};
